import * as React from 'react';
import { useIntl } from 'react-intl';
import { useAuth } from '../auth/AuthContext';
import type { WorkspaceInfo } from '../auth/types';
import {
  resolveCurrentDocumentUrl,
  getCurrentDocumentUrl,
} from '../services/powerpointFileService';
import { workspaceResolverMessages } from '../intl/messages';

export type ResolvedFile = {
  id: string;
  name: string;
  path: string;
  desktopEditUrl?: string | null;
  meta?: { title?: string | null };
};

export type ResolverStatus =
  | 'idle'
  | 'resolving'
  | 'resolved'
  | 'not-synced'
  /**
   * The active workspace contains more than one Showell record referencing
   * the same underlying file. Treated like `not-synced` for routing (no
   * `resolvedFile`, HomeView still renders), but the SyncStatusBar shows a
   * distinct message so the user knows why sync is disabled.
   */
  | 'multiple-in-workspace'
  | 'ambiguous'
  | 'no-document'
  | 'error';

type WorkspaceResolverContextValue = {
  status: ResolverStatus;
  resolvedFile: ResolvedFile | null;
  documentUrl: string | null;
  errorMessage: string | null;
  retry: () => void;
  /**
   * Workspaces that contain the currently open file. Populated only when
   * `status === 'ambiguous'` (more than one workspace matched) so the picker
   * UI can render the choice.
   */
  matchingWorkspaces: WorkspaceInfo[] | null;
  /**
   * Called from the ambiguous picker when the user picks a workspace.
   * Switches to that workspace if it isn't already current, and triggers a
   * re-resolve (the effect's deps wouldn't change if the picked workspace
   * is the current one, so we have to force it).
   */
  pickWorkspace: (app: WorkspaceInfo) => void;
};

const WorkspaceResolverContext =
  React.createContext<WorkspaceResolverContextValue>({
    status: 'idle',
    resolvedFile: null,
    documentUrl: null,
    errorMessage: null,
    retry: () => {},
    matchingWorkspaces: null,
    pickWorkspace: () => {},
  });

function escapeQueryValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

const FILE_FIND_SPECS = {
  id: true,
  name: true,
  path: true,
  desktopEditUrl: true,
  meta: { title: true },
} as const;

export function WorkspaceResolverProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { api, accountId, applications, switchWorkspace } = useAuth();
  const intl = useIntl();

  const [status, setStatus] = React.useState<ResolverStatus>('idle');
  const [resolvedFile, setResolvedFile] = React.useState<ResolvedFile | null>(
    null
  );
  const [documentUrl, setDocumentUrl] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [matchingWorkspaces, setMatchingWorkspaces] = React.useState<
    WorkspaceInfo[] | null
  >(null);
  const [resolveCount, setResolveCount] = React.useState(0);

  const retry = React.useCallback(() => {
    setResolveCount(c => c + 1);
  }, []);

  const pickWorkspace = React.useCallback(
    (app: WorkspaceInfo) => {
      if (app.accountId !== accountId) {
        switchWorkspace(app.accountName, app.accountId, app.displayName);
        // accountId change triggers the resolver effect via deps.
      } else {
        // Same workspace as current — none of the effect's deps would
        // change, so force a re-resolve via the retry counter.
        setResolveCount(c => c + 1);
      }
    },
    [accountId, switchWorkspace]
  );

  // Tracks the last URL we polled — undefined = no poll has run yet
  const lastPollUrlRef = React.useRef<string | null | undefined>(undefined);

  // Tracks the URL we last auto-switched workspace for. Prevents the
  // resolver from clobbering a manual workspace change made by the user
  // (we only auto-detect once per document URL).
  const lastAutoSwitchUrlRef = React.useRef<string | null>(null);

  // Prevents stale async resolutions from applying after a newer one started
  const generationRef = React.useRef(0);

  React.useEffect(() => {
    if (!api) {
      return;
    }

    const capturedApi = api;
    const capturedAccountId = accountId;
    const capturedApplications = applications;
    const generation = ++generationRef.current;

    /**
     * Query a single workspace for files matching `desktopEditUrl`.
     *
     * Trashed files (anywhere under `/.trash/`) are excluded — they
     * shouldn't affect resolution or count toward the "multiple files"
     * check.
     */
    async function findInWorkspace(
      workspaceAccountId: string,
      url: string
    ): Promise<ResolvedFile[]> {
      const result = (await capturedApi.client('fileSystem').findMany({
        query: [
          `[desktopEditUrl='${escapeQueryValue(
            url
          )}'][accountId='${workspaceAccountId}']`,
        ],
        specs: FILE_FIND_SPECS,
      })) as unknown as ResolvedFile[];
      return (result ?? []).filter(f => !f.path?.startsWith('/.trash/'));
    }

    /**
     * Probe every workspace the user has access to. Returns one entry per
     * workspace that contains at least one matching file; `files` may have
     * more than one entry if a single workspace has multiple Showell
     * records pointing at the same underlying file.
     */
    async function probeAllWorkspaces(
      url: string
    ): Promise<{ app: WorkspaceInfo; files: ResolvedFile[] }[]> {
      if (!capturedApplications || capturedApplications.length === 0) {
        return [];
      }
      const out: { app: WorkspaceInfo; files: ResolvedFile[] }[] = [];
      for (const app of capturedApplications) {
        const files = await findInWorkspace(app.accountId, url);
        if (generation !== generationRef.current) {
          return out;
        }
        if (files.length > 0) {
          out.push({ app, files });
        }
      }
      return out;
    }

    async function resolve() {
      setStatus('resolving');
      setResolvedFile(null);
      setErrorMessage(null);
      setMatchingWorkspaces(null);

      const url = await resolveCurrentDocumentUrl();
      if (generation !== generationRef.current) {
        return;
      }

      lastPollUrlRef.current = url;
      setDocumentUrl(url);

      if (!url) {
        setStatus('no-document');
        return;
      }

      if (!capturedAccountId) {
        setStatus('error');
        setErrorMessage('Not signed in');
        return;
      }

      try {
        // Re-resolve for a URL we've already probed (e.g. the user just
        // picked from the ambiguous view): just look in the current
        // workspace. No cross-workspace probe — that would re-surface the
        // picker the user just dismissed.
        if (lastAutoSwitchUrlRef.current === url) {
          const files = await findInWorkspace(capturedAccountId, url);
          if (generation !== generationRef.current) {
            return;
          }
          applyWorkspaceFiles(files);
          return;
        }

        // First time we've seen this URL: probe every workspace.
        const probed = await probeAllWorkspaces(url);
        if (generation !== generationRef.current) {
          return;
        }

        // Mark this URL as probed so subsequent accountId changes don't
        // re-probe (and re-surface the picker after a user pick).
        lastAutoSwitchUrlRef.current = url;

        if (probed.length === 0) {
          setStatus('not-synced');
          return;
        }

        if (probed.length === 1) {
          const [{ app, files }] = probed;
          if (app.accountId !== capturedAccountId) {
            switchWorkspace(app.accountName, app.accountId, app.displayName);
            console.info(
              intl.formatMessage(
                workspaceResolverMessages.autoSwitchedWorkspace,
                { name: app.displayName }
              )
            );
          }
          applyWorkspaceFiles(files);
          return;
        }

        // More than one workspace contains the file → ambiguous, picker.
        setMatchingWorkspaces(probed.map(p => p.app));
        setStatus('ambiguous');
      } catch {
        if (generation !== generationRef.current) {
          return;
        }
        setStatus('error');
        setErrorMessage('Failed to check sync status');
      }
    }

    /**
     * Common tail for "we have N files matching in the active workspace".
     * Treats the >1 case the same as "not synced" (no resolvedFile), with
     * a distinct status so the UI can surface a clearer message: we can't
     * pick which Showell record to sync against, so sync stays off.
     */
    function applyWorkspaceFiles(files: ResolvedFile[]) {
      if (files.length === 0) {
        setStatus('not-synced');
        return;
      }
      if (files.length > 1) {
        setStatus('multiple-in-workspace');
        return;
      }
      setResolvedFile(files[0]);
      setStatus('resolved');
    }

    resolve().catch(() => {
      if (generation === generationRef.current) {
        setStatus('error');
        setErrorMessage('Unexpected error during workspace resolution');
      }
    });
  }, [api, accountId, applications, resolveCount, switchWorkspace, intl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for document URL changes (supports desktop MDI mode where the task
  // pane persists across document switches in the same window).
  React.useEffect(() => {
    if (!api) {
      return;
    }

    const interval = setInterval(() => {
      const currentUrl = getCurrentDocumentUrl();
      // undefined → no poll has run yet, skip comparison
      if (
        lastPollUrlRef.current !== undefined &&
        currentUrl !== lastPollUrlRef.current
      ) {
        lastPollUrlRef.current = currentUrl;
        setResolveCount(c => c + 1);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [api]);

  const value = React.useMemo(
    () => ({
      status,
      resolvedFile,
      documentUrl,
      errorMessage,
      retry,
      matchingWorkspaces,
      pickWorkspace,
    }),
    [
      status,
      resolvedFile,
      documentUrl,
      errorMessage,
      retry,
      matchingWorkspaces,
      pickWorkspace,
    ]
  );

  return (
    <WorkspaceResolverContext.Provider value={value}>
      {children}
    </WorkspaceResolverContext.Provider>
  );
}

export function useWorkspaceResolver(): WorkspaceResolverContextValue {
  return React.useContext(WorkspaceResolverContext);
}
